import axios from 'axios'
import { z, ZodError } from 'zod'

import type { CalculationDomainError } from '@/shared/types/cart'

export class ApiError extends Error {
	readonly status: number | null
	readonly code: string
	readonly raw: unknown
	readonly friendlyError: string | null
	readonly metadata: Record<string, unknown> | undefined

	constructor(
		message: string,
		status: number | null,
		code: string,
		raw: unknown,
		friendlyError: string | null = null,
		metadata?: Record<string, unknown>,
	) {
		super(message)
		this.name = 'ApiError'
		this.status = status
		this.code = code
		this.raw = raw
		this.friendlyError = friendlyError
		this.metadata = metadata
	}
}

// Thrown by calculateCart() when the API returns a structured domain errors array.
// Carries the full errors[] so the calculate thunk can inspect every error code
// (ITMNA, ORDCOUPINV, ITMOOSTK etc.) rather than only the first one.
export class CartApiError extends ApiError {
	readonly errors: CalculationDomainError[]

	constructor(errors: CalculationDomainError[], status: number | null, raw: unknown) {
		super('Cart calculation failed', status, 'CART_API_ERROR', raw)
		this.name = 'CartApiError'
		this.errors = errors
	}
}

const apiErrorEntrySchema = z.object({
	code: z.string().optional(),
	message: z.string().optional(),
	// friendlyError is explicitly null in many API responses — nullish() accepts both null and undefined.
	friendlyError: z.string().nullish(),
	// metadata is explicitly null in OLO error responses (e.g. ORDCCNOPROC from the order API).
	// nullish() = optional + nullable — accepts undefined, null, or a record object.
	metadata: z.record(z.string(), z.unknown()).nullish(),
})

// Accepts both { errors: [...] } (OOC) and top-level [...] (OLO) response shapes.
const apiErrorResponseSchema = z.union([
	z.array(apiErrorEntrySchema),
	z.object({ errors: z.array(apiErrorEntrySchema) }),
])

export function normalizeZodError(error: ZodError): ApiError {
	return new ApiError(error.message, null, 'SCHEMA_ERROR', error)
}

export function normalizeError(error: unknown): ApiError {
	// If already normalized, return as-is to preserve status and other metadata
	if (error instanceof ApiError) {
		return error
	}

	if (axios.isAxiosError(error)) {
		const status = error.response?.status ?? null
		// Guard against malformed responses (HTML error pages, plain strings, null, etc.).
		// Keep responseData for the plain-object message fallback below.
		const responseData =
			typeof error.response?.data === 'object' && error.response.data !== null
				? (error.response.data as Record<string, unknown>)
				: undefined
		// Validate with Zod — handles both { errors: [...] } (OOC) and top-level [...] (OLO).
		// Falls back gracefully when the shape doesn't match either schema.
		const parsed = apiErrorResponseSchema.safeParse(error.response?.data)
		const firstError = parsed.success
			? Array.isArray(parsed.data)
				? parsed.data[0]
				: parsed.data.errors[0]
			: undefined
		const message =
			firstError?.message ??
			(typeof responseData?.message === 'string' ? responseData.message : undefined) ??
			error.message
		// Prefer the backend domain code from the response body; fall back to the Axios transport code.
		const code = firstError?.code ?? error.code ?? 'UNKNOWN'
		const friendlyError = firstError?.friendlyError ?? null

		return new ApiError(
			message,
			status,
			code,
			error,
			friendlyError,
			firstError?.metadata ?? undefined,
		)
	}

	if (error instanceof Error) {
		return new ApiError(error.message, null, 'UNKNOWN', error)
	}

	return new ApiError('An unexpected error occurred', null, 'UNKNOWN', error)
}
